import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import api from '@/api';
import { isHttpError } from '@/libs/api-helper';
import type { OnDemandRoute } from '@/@types/on-demand-route';

/**
 * On-demand routes of the network (features `on_demand` and `device_event_sourcing`),
 * loaded once per group. Without the features the list stays empty and the app
 * shows no on-demand line.
 */
export const useOnDemandStore = defineStore('on-demand', () => {
  const routes = ref<OnDemandRoute[]>([]);
  const loading = ref<boolean>(false);
  /** The route its view puts on the map, with its stops; undefined while it loads. */
  const shownRoute = ref<OnDemandRoute>();
  /** The vehicles on the map are last known positions: the polling no longer answers. */
  const vehiclesStale = ref<boolean>(false);

  // The routes of a group left in the meantime must not land on the one displayed.
  let loadRun = 0;
  let routesGroupId: string | undefined;

  const routesById = computed<Map<string, OnDemandRoute>>(
    () => new Map(routes.value.map(route => [route.route_id, route])),
  );
  const routeIds = computed<string[]>(() => routes.value.map(route => route.route_id));

  /**
   * The `403 LOCKED_FEATURE` of a network without the features is the expected answer
   * for most of them: it must not reach Datadog on every launch. A network failure is
   * already filtered as noise; any other error is a real signal.
   */
  async function loadRoutes(groupId: string) {
    const run = ++loadRun;
    routesGroupId = groupId;
    routes.value = [];
    loading.value = true;
    try {
      const answer = await api.getOnDemandRoutes(groupId);
      if (run === loadRun) routes.value = answer;
    } catch (e) {
      if (run === loadRun && !(isHttpError(e) && (e as Error).message.startsWith('403:'))) {
        console.warn('On-demand routes unavailable, none is listed:', e);
      }
    } finally {
      if (run === loadRun) loading.value = false;
    }
  }

  function getRoute(routeId: string): OnDemandRoute | undefined {
    return routesById.value.get(routeId);
  }

  /** The detail of one route, also refreshed in the list of its network. Throws on failure. */
  async function fetchRoute(groupId: string, routeId: string): Promise<OnDemandRoute> {
    const route = await api.getOnDemandRoute(groupId, routeId);
    // Ids are only unique within a network: another one may have been loaded meanwhile.
    if (groupId !== routesGroupId) return route;
    const index = routes.value.findIndex(candidate => candidate.route_id === routeId);
    if (index >= 0) routes.value[index] = route;
    return route;
  }

  return { routes, routeIds, loading, shownRoute, vehiclesStale, loadRoutes, getRoute, fetchRoute };
});
